import type { Contact } from '@/api/queries/conversations';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactForm } from './ContactForm';
import { ContactList } from './ContactList';
import { useContactManagement } from './useContactManagement';

interface ManageContactsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ManageContacts({ isOpen, onClose }: ManageContactsProps) {
  const { t } = useTranslation('conversations');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  const {
    createContact,
    updateContact,
    deleteContact,
    isLoading: isMutationContacts,
  } = useContactManagement();

  const handleAddContact = async (
    contactData: Omit<Contact, 'id' | 'owner_user_id' | 'created_at' | 'updated_at'>
  ) => {
    await createContact(contactData);
    setShowAddForm(false);
  };

  const handleEditContact = async (
    contactData: Omit<Contact, 'id' | 'owner_user_id' | 'created_at' | 'updated_at'>
  ) => {
    if (!editingContact) return;

    await updateContact(editingContact.id, contactData);
    setEditingContact(null);
  };

  const handleDeleteContact = async (contactId: string) => {
    await deleteContact(contactId);
    setEditingContact(null);
  };

  const handleEditClick = (contact: Contact) => {
    setEditingContact(contact);
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setEditingContact(null);
  };

  const isFormOpen = showAddForm || !!editingContact;

  const addContactButton = (
    <Button
      onClick={() => setShowAddForm(true)}
      variant="outline"
      className="bg-orange-500 text-white hover:bg-orange-600 hover:text-white"
      data-testid="manage-contact-add"
    >
      {t('contactManagement.addContact')}
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader className="pb-4">
          <DialogTitle data-testid="manage-contact-title">
            {t('contactManagement.manageContacts')}
          </DialogTitle>
        </DialogHeader>

        {/* Content */}
        {isFormOpen ? (
          <ContactForm
            onCancel={handleCloseForm}
            onSave={editingContact ? handleEditContact : handleAddContact}
            onDelete={handleDeleteContact}
            contact={editingContact}
            isLoading={isMutationContacts}
          />
        ) : (
          <ContactList
            onContactClick={handleEditClick}
            showEditButtons
            addContactButton={addContactButton}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
